/**
 * 덱 산출 요청 — **무엇으로 덱을 만들지 정하는 단일 지점**.
 *
 * 공용 함수(`buildEditedDeck`)를 만든 것만으로는 산출물이 일치하지 않았다.
 * 그 함수를 부르는 **인자 조립**이 호출부마다 손배선돼 있었기 때문이다(실측):
 *
 *  - `source` 판정이 두 정의로 5곳에 흩어져 공백 원문에서 검증본 260,943 B vs
 *    실행본 5 B 로 갈렸다
 *  - restart 판정이 세 정의(`taskMode` / `card100.problemType` / `restartBaseline`)
 *    로 갈려 같은 모델이 56,046 B 와 329,950 B 를 냈다
 *  - co-sim 만 `baseline`·`modelId` 를 안 넘겨 단일 실행과 제출본이 달랐다
 *
 * 호출부는 이제 "어느 모델을" 만 말하고, 어떤 원문·기준선·모드를 쓸지는 여기가
 * 정한다. 인자를 빠뜨릴 자리가 없다.
 */
import type { Node, Edge } from 'reactflow';
import type { DeckOrigin } from '@/domain/mars/input/merge/plan';
import type { GlobalSettings } from '@/domain/mars/globals/settings';
import type { MARSNodeData } from '@/types/mars';
import type { MarsEditBaseline } from './marsPersist';

/** 덱을 만들 때 쓰는 모델 쪽 입력 */
export interface MarsDeckModel {
  id: string;
  name?: string;
  /** 저장된 원문(.i) */
  textContent?: string | null;
}

/** 덱을 만들 때 쓰는 편집 세션 쪽 입력 */
export interface MarsDeckSession {
  nodes: Node<MARSNodeData>[];
  edges: Edge[];
  globalSettings?: GlobalSettings;
  restartBaseline?: unknown;
  /** 모델 메타데이터의 작업 모드 */
  taskMode?: string;
  /** 글로벌 설정의 문제 유형(카드 100) */
  problemType?: string;
  baseline?: MarsEditBaseline | null;
  /** 편집을 싣는가(에디터의 «저장 안 됨») — 편집 반영이 저장과 같은 원문(신원 줄 포함)을 내게 */
  edited?: boolean;
}

export interface MarsDeckRequest {
  nodes: Node<MARSNodeData>[];
  edges: Edge[];
  projectName: string;
  globalSettings?: GlobalSettings;
  restartBaseline?: unknown;
  /** 정규화된 작업 모드 — 'restart' 이면 오버라이드 덱이다 */
  taskMode: string | undefined;
  /** 실제로 쓸 원문 — 없으면 첫 시드 */
  source: string | undefined;
  baseline?: MarsEditBaseline | null;
  modelId: string;
  edited?: boolean;
}

/**
 * "원문이 있는가" 판정 — **한 정의뿐이다**.
 * 공백·개행만 있는 값은 원문이 아니다(엔진이 읽을 카드가 하나도 없다).
 */
export function effectiveSource(textContent: string | null | undefined): string | undefined {
  return textContent?.trim() ? textContent : undefined;
}

/**
 * RESTART 판정 — **한 정의뿐이다**.
 *
 * 판정 지식이 세 곳에 흩어져 있었고, 텍스트 커밋으로 덱의 `100 restart` 가
 * 설정에 실려도 `metadata.taskMode` 는 그대로 남아 두 값이 어긋났다. 엔진이
 * 보는 것은 카드 100 이므로 **그쪽이 우선**이고, 없을 때만 메타데이터를 쓴다.
 */
export function isRestartMode(
  session: Pick<MarsDeckSession, 'taskMode' | 'problemType' | 'globalSettings'>,
): boolean {
  // 카드 100 은 세션 글로벌 설정에 이미 있다 — 호출부가 problemType 을 따로
  // 복사해 줘야만 우선하면, 안 준 호출부(내보내기·검증·실행 전부, R3 실측)는
  // 낡은 taskMode 로 떨어진다. 여기서 중앙 유도한다.
  const problemType =
    session.problemType ??
    (session.globalSettings as { card100?: { problemType?: string } } | undefined)?.card100
      ?.problemType;
  if (problemType) return problemType === 'restart';
  return session.taskMode === 'restart';
}

/** 요청 조립 — 호출부가 인자를 고르지 않는다 */
/**
 * 이 모델의 제출 덱이 **무엇에서 나오는가** — 실행 가능 판정의 정본.
 *
 * 이 술어가 없던 동안 두 곳이 서로 다른 답을 냈다(R7 blocker 6):
 * `participantKinds.readiness` 는 "원문이 있으면 낼 수 있다"고 하는데
 * `buildMarsRunDeck` 은 원문 유무와 무관하게 생성기를 먼저 불러
 * `nodes.length === 0` 에서 `No components added` 로 죽었다 —
 * **시작 버튼은 열려 있고 누르면 죽는** 상태.
 *
 * - `generated` : RESTART(오버라이드 덱) 또는 원문이 없고 그래프가 있다
 * - `source`    : 원문이 있다 — 원문이 제출본이고 편집만 반영한다(층 계약 4)
 * - `none`      : 원문도 그래프도 없다 — 낼 수 있는 덱이 없다
 */
export function deckOrigin(input: {
  taskMode: string | undefined;
  problemType?: string;
  /** 카드 100 유도용 — problemType 을 따로 안 넘겨도 같은 판정을 탄다 */
  globalSettings?: MarsDeckSession['globalSettings'];
  textContent: string | null | undefined;
  nodeCount: number;
}): DeckOrigin {
  if (
    isRestartMode({
      taskMode: input.taskMode,
      problemType: input.problemType,
      globalSettings: input.globalSettings,
    })
  )
    return 'generated';
  if (effectiveSource(input.textContent)) return 'source';
  return input.nodeCount > 0 ? 'generated' : 'none';
}

export function marsDeckRequest(
  model: MarsDeckModel,
  session: MarsDeckSession,
  projectName: string,
): MarsDeckRequest {
  return {
    nodes: session.nodes,
    edges: session.edges,
    projectName,
    globalSettings: session.globalSettings,
    restartBaseline: session.restartBaseline,
    taskMode: isRestartMode(session) ? 'restart' : session.taskMode,
    source: effectiveSource(model.textContent),
    baseline: session.baseline,
    modelId: model.id,
    edited: session.edited,
  };
}

/**
 * **요청 → 편집 반영 덱**. 세 경로(내보내기·검증·실행)가 이 한 줄을 쓴다.
 *
 * 이 정본을 만들어 놓고도 소비자가 0이었다(2026-08-31 실측) — 호출부는 여전히
 * 인자를 각자 조립했다. 조립이 갈리면 산출물이 갈린다는 것이 이 파일 머리주석의
 * 실측이고(검증본 260,943 B vs 실행본 5 B), 그래서 조립을 여기 묶는다.
 * `undefined` 면 기준선이 없거나 남의 모델 것이라 **원문을 그대로 쓴다**는 뜻이다.
 */
export function editedDeckFor(
  request: MarsDeckRequest,
  build: (input: {
    source: string;
    nodes: MarsDeckRequest['nodes'];
    globalSettings?: MarsDeckRequest['globalSettings'];
    baseline?: MarsDeckRequest['baseline'];
    modelId?: string;
    edited?: boolean;
  }) => { source: string; failures: unknown[] } | undefined,
): { source: string; failures: unknown[] } | undefined {
  if (!request.source || request.taskMode === 'restart') return undefined;
  return build({
    source: request.source,
    nodes: request.nodes,
    globalSettings: request.globalSettings,
    baseline: request.baseline,
    modelId: request.modelId,
    edited: request.edited,
  });
}
